import { colors, hexToRgba, intl, ui } from '@beelday/common'
import { useSelector } from 'app-redux'
import { Config } from 'common/config'
import { UUID } from 'common/types'
import KnowledgeCheckTimer from 'features/knowledge-checks/presentation/knowledge-check-timer'
import { useTraineeKnowledgeCheck } from 'features/knowledge-checks/use-trainee-knowledge-check'
import { ConfiguredArchivedActivity } from './configured-archived-activity'
import { ConfiguredKnowledgeCheckOutcome } from './configured-knowledge-check-outcome'
import { toggleOption } from './model/answers'
import { PresentationIllustration } from './presentation/illustrations'
import { KnowledgeCheckView } from './presentation/knowledge-check-view'
import { Confirmation, Rest, StateActions } from './presentation/primitives'

type Props = {
	knowledgeCheckId: UUID
}

/**
 * A knowledge check on the phone: one question per screen, then the score and
 * the participant's own answers (see `ConfiguredKnowledgeCheckOutcome`).
 *
 * The review is the projector's alone. When the trainer opens it, the phone
 * says so instead of mirroring it, and stops showing answers of any kind - the
 * room looks at one screen while the trainer walks through it.
 */
export const ConfiguredKnowledgeCheck = ({
	knowledgeCheckId,
}: Props): JSX.Element | null => {
	const isReview = useSelector(state => state.knowledgeChecks.isReview)
	const {
		knowledgeCheck,
		error,
		busy,
		reload,
		onAnswer,
		onPrev,
		onNext,
		onFinish,
	} = useTraineeKnowledgeCheck(knowledgeCheckId)

	if (isReview) {
		return (
			<Rest
				icon={<PresentationIllustration />}
				title={
					<intl.Translate>
						in_person.mobile.knowledge_check.review
					</intl.Translate>
				}
				description={
					<intl.Translate>
						in_person.mobile.knowledge_check.review.description
					</intl.Translate>
				}
				testId="in_person.mobile.knowledge_check.review"
			/>
		)
	}

	const failure = (
		<Confirmation
			title={<intl.Translate>knowledge_check.error.load</intl.Translate>}
			icon={null}
			background={hexToRgba(colors.lipstick, 0.1)}
			testId="in_person.mobile.knowledge_check.error"
		>
			<StateActions>
				<ui.LinkButton type="button" onClick={reload}>
					<intl.Translate>in_person.mobile.retry</intl.Translate>
				</ui.LinkButton>
			</StateActions>
		</Confirmation>
	)

	if (error) {
		return failure
	}

	if (!knowledgeCheck) {
		return null
	}

	if (knowledgeCheck.doneInPreviousSession) {
		return (
			<ConfiguredArchivedActivity
				toolType="KNOWLEDGE_CHECK"
				title={knowledgeCheck.name}
			/>
		)
	}

	if (knowledgeCheck.state.finished) {
		return (
			<ConfiguredKnowledgeCheckOutcome
				knowledgeCheckId={knowledgeCheckId}
				useCorrectAnswers={knowledgeCheck.useCorrectAnswers}
			/>
		)
	}

	const { questions, state } = knowledgeCheck
	const index = questions.findIndex(q => q.id === state.currentQuestion)
	const question = questions[index]

	// A check with no questions, or a server pointer to a question this copy
	// does not have: there is nothing sensible to answer, so offer a re-read.
	if (!question) {
		return failure
	}

	const selected = state.answers?.[question.id] ?? []
	const multiple = question.type === 'MULTI_SELECT'

	return (
		<KnowledgeCheckView
			index={index}
			total={questions.length}
			question={question.question}
			imageUrl={
				question.image?.fileId
					? `${Config.sessionPlanningURL}/files/${question.image.fileId}`
					: undefined
			}
			options={question.options.map(option => ({
				id: option.id,
				text: option.answer,
			}))}
			multiple={multiple}
			selected={selected}
			busy={busy}
			timer={
				knowledgeCheck.maxDurationInSeconds && knowledgeCheck.startTime ? (
					<KnowledgeCheckTimer
						startTime={knowledgeCheck.startTime}
						maxDurationInSeconds={knowledgeCheck.maxDurationInSeconds}
					/>
				) : null
			}
			onToggle={id =>
				onAnswer(question.id, toggleOption(selected, id, multiple))
			}
			onPrev={onPrev}
			onNext={onNext}
			onFinish={() => onFinish(question.id, selected)}
		/>
	)
}

export default ConfiguredKnowledgeCheck
